import axios from 'axios'

import type { MerchantConfigs } from '@/types/types'
import type { LoyaltyMerchantFormConfigs } from '@/types/global'

const merchantConfigsForm = ref<LoyaltyMerchantFormConfigs>(null)

export default function useMerchantConfigs() {
  const { user } = useLocalStorage()
  const merchantConfigs = ref<MerchantConfigs>(null)
  const { t } = useI18n()
  const notification = useNotification()

  const getMerchantConfigs = async (): Promise<MerchantConfigs> => {
    try {
      const { data } = await axios.get(`/merchants/${user.value.merchant.id}/configs`)
      merchantConfigs.value = data.data
      return data.data
    } catch {
      notification.create('error', {
        text: t('pages.settings.merchantForm.notifications.get.merchantConfigsError'),
        timeout: 3000
      })
    }
  }

  merchantConfigsForm.value = {
    checkinPoints: {
      label: t('pages.settings.merchantForm.config.checkinPoints'),
      value: 10,
      type: 'number',
      required: true,
      min: 0
    },
    bonusPoints: {
      label: t('pages.settings.merchantForm.config.bonusPoints'),
      value: 10,
      type: 'number',
      min: 0,
      required: true
    },
    loyaltyOrderMinimumAmount: {
      label: t('pages.settings.merchantForm.config.loyaltyOrderMinimumAmount'),
      value: 0,
      type: 'number',
      min: 0,
      required: true
    },
    hasPointsByCurrency: {
      label: t('pages.settings.merchantForm.config.hasPointsByCurrency'),
      value: false,
      type: 'toggle',
      required: true
    },
    loyaltyPointsByCurrencyUnit: {
      label: t('pages.settings.merchantForm.config.loyaltyPointsByCurrencyUnit'),
      value: 10,
      type: 'number',
      min: 0,
      required: true
    },
    delayBetweenCheckins: {
      label: t('pages.settings.merchantForm.config.delayBetweenCheckins'),
      value: 7,
      type: 'number',
      required: true,
      min: 0
    },
    rewardTimeout: {
      label: t('pages.settings.merchantForm.config.rewardTimeout'),
      value: 15,
      type: 'number',
      min: 0,
      required: true
    },
    tabletPointsClaimingDelay: {
      label: t('pages.settings.merchantForm.config.pointsClaimingDelay'),
      value: 10,
      type: 'number',
      min: 0,
      required: true
    },
    hasExternalCustomers: {
      label: t('pages.settings.merchantForm.config.hasExternalCustomers'),
      value: false,
      type: 'toggle',
      required: true
    },
    hasPointsCanceledOnOrderCancelation: {
      label: t('pages.settings.merchantForm.config.hasPointsCanceledOnOrderCancelation'),
      value: false,
      type: 'toggle',
      required: false
    },
    integrationPointsClaimingTitle: {
      label: t('pages.settings.merchantForm.text.orderTextsFields.integrationPointsClaimingTitle.label'),
      value: t('pages.settings.merchantForm.text.orderTextsFields.integrationPointsClaimingTitle.value'),
      type: 'text',
      required: true
    },
    integrationPointsClaimingSubtitle: {
      label: t('pages.settings.merchantForm.text.orderTextsFields.pointsClaimingSubtitle.label'),
      value: t('pages.settings.merchantForm.text.orderTextsFields.pointsClaimingSubtitle.value'),
      type: 'text',
      required: true
    },
    loyaltyDeliveryOrderMessageChannel: {
      label: '',
      value: 'none',
      type: '',
      required: true
    }
  }

  const updateMerchantConfigsForm = async () => {
    const data = Object.entries(merchantConfigsForm.value).reduce((acc, [key, obj]) => {
      if (key !== 'integrationPointsClaimingTitle' && key !== 'integrationPointsClaimingSubtitle') {
        acc[key] = obj.value
      }
      return acc
    }, {})

    putMerchantConfigs(data as MerchantConfigs)
  }

  const putMerchantConfigs = async (merchantConfigs: MerchantConfigs) => {
    try {
      await axios.put(`/merchants/${user.value.merchant.id}/configs`, {
        data: merchantConfigs
      })
    } catch (error) {
      notification.create('error', {
        text: t('pages.settings.merchantForm.notifications.updateMerchantConfig.error'),
        timeout: 3000
      })
      throw error
    }
  }

  const parseConfigFields = (items: LoyaltyMerchantFormConfigs) => {
    const pointsFields = [
      'bonusPoints',
      'delayBetweenCheckins',
      'fixedPointsAmount',
      'minimumAmount',
      'checkinPoints',
      'hasPointsCanceledOnOrderCancelation'
    ]
    const integrationsFields = ['loyaltyPointsByCurrencyUnit', 'loyaltyOrderMinimumAmount', 'hasPointsByCurrency']
    const notificationsFields = ['integrationPointsClaimingTitle', 'integrationPointsClaimingSubtitle']
    const notificationsHiddenFields = ['loyaltyDeliveryOrderMessageChannel']
    const tabletFields = ['tabletPointsClaimingDelay', 'rewardTimeout', 'hasExternalCustomers']

    const fieldMapping: { [key: string]: string[] } = {
      points: pointsFields,
      integrations: integrationsFields,
      notifications: notificationsFields,
      notificationsHidden: notificationsHiddenFields,
      tablet: tabletFields
    }

    const object = {
      points: [],
      tablet: [],
      integrations: [],
      notifications: [],
      notificationsHidden: [],
      qrCode: []
    }

    Object.entries(items).forEach(([key, value]) => {
      Object.entries(fieldMapping).some(([field, fields]) => {
        if (fields.includes(key)) {
          object[field].push(value)
          return true
        }
        return false
      })
    })

    return object
  }

  return {
    getMerchantConfigs,
    merchantConfigs,
    merchantConfigsForm,
    updateMerchantConfigsForm,
    parseConfigFields,
    putMerchantConfigs
  }
}
